import { graphql, useStaticQuery } from 'gatsby';
import { DateTime } from 'luxon';
import React, { FC } from 'react';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import fullWidth from '@ui/styled/FullWidth';
import padded from '@ui/styled/Padded';

import useSubscriptionInfo from '@hooks/useSubscriptionInfo';

import { interpolate } from '@utils/butter';
import { keysToCamel } from '@utils/butter';

import { IPageProps } from '@folx-types/flow-steps';

import SwitchToMonthlyMembership, {
	ISwitchToMonthlyMembershipPageContent,
} from './SwitchToMonthlyMembership.component';

interface ISwitchToMonthlyMembershipPageProps extends IPageProps {}

const formatDate = (date: string | undefined, format = 'DD') =>
	date
		? DateTime.fromISO(date).toFormat(format, {
				locale: 'en',
		  })
		: null;

const lessThanSixWeeksFromNow = (date: string | undefined) => {
	if (!date) return false;

	const now = DateTime.now();
	const next = DateTime.fromISO(date);
	return next.diff(now).as('weeks') < 6;
};

const FullWidthColumn = fullWidth(FlexColumn);
const PaddedColumn = padded(FlexColumn);
const CardSkeleton = styled(Skeleton)`
	height: 23rem;
`;

const SwitchToMonthlyMembershipSkeleton = () => (
	<FullWidthColumn gap="lg">
		<Skeleton height="md" fullWidth />
		<CardSkeleton fullWidth structure="single" />
		<PaddedColumn gap="sm" paddingLeft="md" paddingRight="md">
			<FlexColumn gap="xs">
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" width="xxxxxl" />
			</FlexColumn>
			<Skeleton height="xs" width="xxxxl" />
		</PaddedColumn>
		<PaddedColumn gap="xs" paddingLeft="md" paddingRight="md">
			<Skeleton height="lg" fullWidth />
			<Skeleton height="sm" fullWidth />
		</PaddedColumn>
	</FullWidthColumn>
);
const SwitchToMonthlyMembershipPage: FC<ISwitchToMonthlyMembershipPageProps> = ({ path }) => {
	const { pageContent, sharedMembershipContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mssp-switch-to-monthly-membership-page" }) {
				title
				fineprint_copy
				primary_cta
				secondary_cta
			}
			sharedMembershipContent: butterPage(slug: { eq: "shared-folx-membership-information" }) {
				folx_membership_option_monthly {
					title
					price
					badge
					description
					disclaimer
				}
			}
		}
	`);

	const { membership, loading: subscriptionLoading } = useSubscriptionInfo();

	const lastDay =
		membership?.membershipSubscription.nextSubscriptionRenewalDate ??
		membership?.membershipSubscription.upcomingInvoice.nextPaymentAttempt;

	const refinedContent: ISwitchToMonthlyMembershipPageContent = keysToCamel({
		...pageContent,
		...sharedMembershipContent,
	}) as ISwitchToMonthlyMembershipPageContent;

	refinedContent.folxMembershipOptionMonthly.description = interpolate(
		refinedContent.fineprintCopy,
		{
			yearlyMembershipEndDate: formatDate(lastDay),
		}
	);

	return (
		<LoadingWrapper loading={subscriptionLoading} skeleton={<SwitchToMonthlyMembershipSkeleton />}>
			<SwitchToMonthlyMembership
				pageContent={refinedContent}
				allowSwitch={lessThanSixWeeksFromNow(lastDay)}
				path={path}
			/>
		</LoadingWrapper>
	);
};

export default SwitchToMonthlyMembershipPage;
